import { debugGlobals } from '$lib/debug';

const TRACKING_CONTEXT_PREFIX = 'perspective_tracking_context_';

let globals: { [key: string]: any } = {};
let currentCampaignId: string | undefined;

const getStorageKey = (): string | undefined => {
    if (!currentCampaignId) return undefined;
    return `${TRACKING_CONTEXT_PREFIX}${currentCampaignId}`;
};

const persistToSessionStorage = (): void => {
    const key = getStorageKey();

    if (!key) return;

    try {
        sessionStorage.setItem(key, JSON.stringify(globals));
    } catch (err) {
        console.warn('Failed to persist tracking context to sessionStorage:', err);
    }
};

/**
 * Set the campaign scope for sessionStorage persistence.
 * Must be called before the first setGlobals() call.
 * Restores any previously persisted tracking context from sessionStorage so it
 * survives JS module re-initialisation — which can happen in any browser on a
 * bfcache restore, hard reload, or unexpected navigation event.
 */
export const initGlobalsCampaignScope = (campaignId: string): void => {
    currentCampaignId = campaignId;
    globals = {};

    try {
        const key = `${TRACKING_CONTEXT_PREFIX}${campaignId}`;
        const stored = sessionStorage.getItem(key);

        if (stored) {
            globals = JSON.parse(stored);
        }
    } catch (err) {
        console.warn('Failed to load tracking context from sessionStorage:', err);
    }
};

const readFromSessionStorage = (): { [key: string]: any } => {
    const key = getStorageKey();

    if (!key) return globals;

    try {
        const stored = sessionStorage.getItem(key);

        if (!stored) return globals;

        return JSON.parse(stored);
    } catch (err) {
        console.warn('Failed to read tracking context from sessionStorage:', err);
        return globals;
    }
};

export const getGlobals = () => readFromSessionStorage();

export const setGlobals = (newGlobals: { [key: string]: any }) => {
    debugGlobals('-> setGlobals', newGlobals);

    globals = Object.assign({}, readFromSessionStorage(), newGlobals);
    persistToSessionStorage();

    return globals;
};

export const getTrackingGlobals = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { context, ...rest } = readFromSessionStorage();

    return rest;
};
